/**
 * @fileoverview Auth API Contract (Contract-First Design)
 * @module types/contracts
 *
 * Complete API contract for the Auth module.
 * Single source of truth for all authentication-related API definitions.
 */

import { z } from 'zod';
import { createContract, extractRoutes, ApiSuccessResponseSchema } from './contract.utils';
import { UserSchema } from './users.contract';

// ============================================================================
// SCHEMAS (Data Structure & Validation)
// ============================================================================

/**
 * Admin Login Request Schema
 */
export const AdminLoginSchema = z.object({
	email: z.email('Invalid email format'),
	password: z.string().min(8, 'Password must be at least 8 characters'),
});
export type AdminLoginPayload = z.infer<typeof AdminLoginSchema>;

/**
 * Auth Data Schema
 * The actual authentication data returned by the service
 */
export const AuthDataSchema = z.object({
	user: UserSchema,
	sessionId: z.string(),
	csrfToken: z.string(),
});
export type AuthData = z.infer<typeof AuthDataSchema>;

/**
 * Auth API Response Schema (wrapped for API)
 * API response: { success: true, data: AuthData, timestamp: string }
 */
export const AuthApiResponseSchema = ApiSuccessResponseSchema(AuthDataSchema);
export type AuthApiResponse = z.infer<typeof AuthApiResponseSchema>;

/**
 * Current User API Response Schema (wrapped for API)
 * API response: { success: true, data: User | null, timestamp: string }
 */
export const CurrentUserApiResponseSchema = ApiSuccessResponseSchema(UserSchema.nullable());
export type CurrentUserApiResponse = z.infer<typeof CurrentUserApiResponseSchema>;

/**
 * Logout Data Schema
 */
const LogoutDataSchema = z.object({ message: z.string() });

/**
 * Logout API Response Schema (wrapped for API)
 * API response: { success: true, data: { message: string }, timestamp: string }
 */
export const LogoutApiResponseSchema = ApiSuccessResponseSchema(LogoutDataSchema);
export type LogoutApiResponse = z.infer<typeof LogoutApiResponseSchema>;

// ============================================================================
// API CONTRACT (Routes, Methods, Request/Response)
// ============================================================================

/**
 * Auth API Contract
 *
 * Defines the complete API surface for the Auth module.
 * Both frontend and backend use this as the single source of truth.
 */
export const AUTH_API_CONTRACT = createContract('/auth', {
	/**
	 * POST /auth/admin/login
	 * Admin login
	 */
	adminLogin: {
		method: 'POST' as const,
		path: '/admin/login',
		body: AdminLoginSchema,
		response: AuthApiResponseSchema,
	},

	/**
	 * POST /auth/logout
	 * Logout (destroy session)
	 */
	logout: {
		method: 'POST' as const,
		path: '/logout',
		response: LogoutApiResponseSchema,
	},

	/**
	 * GET /auth/me
	 * Get current authenticated user
	 */
	getCurrentUser: {
		method: 'GET' as const,
		path: '/me',
		response: CurrentUserApiResponseSchema,
	},
});

/**
 * Route Constants for Controllers
 * Use these in @Controller() and @Get/@Post/@Delete decorators
 */
export const AUTH_ROUTES = extractRoutes(AUTH_API_CONTRACT);

// ============================================================================
// TYPE EXPORTS
// ============================================================================

/**
 * Export all types for use in frontend and backend
 */
export type AuthApiContract = typeof AUTH_API_CONTRACT;
